import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { toast } from '../store/toastStore';
import AuthLayout from '../components/AuthLayout';

const Register: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const register = useAuthStore((s) => s.register);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);
  const user = useAuthStore((s) => s.user);
  const theme = useThemeStore((s) => s.theme);

  const navigate = useNavigate();
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (user && !hasNavigated.current) {
      hasNavigated.current = true;
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (password !== confirmPassword) {
      const msg = 'Passwords do not match.';
      setValidationError(msg);
      toast.warning('Validation error', msg);
      return;
    }

    if (password.length < 6) {
      const msg = 'Password must be at least 6 characters.';
      setValidationError(msg);
      toast.warning('Validation error', msg);
      return;
    }

    setIsSubmitting(true);
    try {
      await register(email, password, displayName);
      // Toast shown inside authStore.register()
    } catch {
      // Error toast shown inside authStore.register()
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = validationError || error;

  return (
    <AuthLayout title="Create your account" subtitle="Start chatting with AI today">
      <form onSubmit={handleSubmit} className="space-y-4">
        {displayError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm"
          >
            {displayError}
          </motion.div>
        )}

        <div>
          <label
            htmlFor="displayName"
            className={`block text-sm font-medium mb-1.5 ${
              theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
            }`}
          >
            Full name
          </label>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="input-field"
            placeholder="Enter your name"
            required
            autoFocus
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className={`block text-sm font-medium mb-1.5 ${
              theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
            }`}
          >
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field"
            placeholder="Enter your email"
            required
            autoComplete="email"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className={`block text-sm font-medium mb-1.5 ${
              theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
            }`}
          >
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field pr-10"
              placeholder="Create a password"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
              }`}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className={`block text-sm font-medium mb-1.5 ${
              theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
            }`}
          >
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input-field"
            placeholder="Confirm your password"
            required
            autoComplete="new-password"
          />
        </div>

        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          type="submit"
          disabled={isSubmitting || !email || !password || !displayName || !confirmPassword}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Creating account...
            </>
          ) : (
            'Continue'
          )}
        </motion.button>

        <p
          className={`text-center text-sm ${
            theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
          }`}
        >
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-gpt-green hover:text-gpt-green-hover transition-colors font-medium"
          >
            Log in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default Register;